// src/prototype/components/Collapse.tsx
// Accordion section used for the on-demand analytical depth beneath the Decision Brief
// and on the Initiative Command Center.
import React, { useId, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

export interface CollapseProps {
  /** Leading icon (typically a lucide icon at w-3.5 h-3.5). */
  icon: React.ReactNode;
  /** Uppercase eyebrow title. */
  title: string;
  /** Optional meta line beside the title, e.g. "3 identified · top severity High". */
  meta?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

export const Collapse: React.FC<CollapseProps> = ({ icon, title, meta, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  const Chevron = open ? ChevronUp : ChevronDown;
  return (
    <div className="bg-white rounded-xl border border-[#e5e7eb]">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full flex items-center gap-2.5 px-5 py-3.5 text-left group"
      >
        <span className="text-[#18365E] shrink-0">{icon}</span>
        <span className="text-xs font-semibold text-[#18365E] uppercase tracking-wide">{title}</span>
        {meta && <span className="text-[10px] text-[#717182] font-medium">{meta}</span>}
        <Chevron className="ml-auto w-4 h-4 text-[#717182] group-hover:text-[#FF6E4C] transition-colors shrink-0" />
      </button>
      {open && (
        <div id={panelId} className="px-5 pb-5">
          {children}
        </div>
      )}
    </div>
  );
};

export default Collapse;
